/**
 * Per-brand sub-region card per scope §6.1.3 (post-clarification commit
 * 2868958). Atlas Day 2 Phase 3 + Atlas Day 3a P1 cross-cell pointer wiring.
 *
 * Surface: "ISG carries N brands; K active for buying. MAP enforcement
 * configured per brand → MAP rule above."
 *
 * Cross-cell pointer: "→ MAP enforcement" wired per Atlas Day 3a P1 —
 * clickable button with stopPropagation; navigates to map-enforcement
 * inspect. Card body navigates to per-brand inspect. Refactored from
 * <button> to <div role="button"> per Day 3a cross-cell pointer pattern.
 */

import type { JSX, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'lucide-react';
import type { BrandSubstrate } from '../../../lib/atlas/perEntitySubstrate';

export interface PerBrandCardProps {
  substrate: BrandSubstrate;
}

export default function PerBrandCard({ substrate }: PerBrandCardProps): JSX.Element {
  const navigate = useNavigate();
  const { total, active, enforcesMap } = substrate;
  // MAP enforcement clause is conditional on enforces_map count (matches Day 1
  // D5 conditional render pattern; D2 lock 2026-05-27 substrate-state-conditional)
  const mapClause =
    enforcesMap > 0
      ? `MAP enforcement configured on ${enforcesMap} ${enforcesMap === 1 ? 'brand' : 'brands'} → MAP rule above.`
      : 'MAP enforcement not yet configured on any brand.';
  const goPrimary = (): void => {
    navigate('/admin/engine-atlas/inspect/per-brand');
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goPrimary();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      data-sub-region-id="per-brand"
      onClick={goPrimary}
      onKeyDown={onKey}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <Tag size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink-bright">Per-brand</h3>
            <button
              type="button"
              data-cross-cell-pointer="map-enforcement"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/admin/engine-atlas/inspect/map-enforcement');
              }}
              className="text-xs text-ink-muted hover:text-accent transition-colors cursor-pointer"
            >
              → MAP enforcement
            </button>
          </div>
          <p className="text-xs text-ink-secondary mt-1">
            ISG carries {total} {total === 1 ? 'brand' : 'brands'}; {active} active for buying.{' '}
            {mapClause}
          </p>
        </div>
      </div>
    </div>
  );
}
